import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { skipToken } from '@reduxjs/toolkit/query';
import { HoursInterval, Outage } from '../types';
import { getIntervalsForDay, getLastIntervalIndex, getOutagesGroupedByDays } from '../utils';
import { useFetchOutageListQuery, useLazyFetchOutageContextQuery } from '../service';
import { WebsiteIndexEntry } from '../../deepAnalysis/websiteIndexEntry/types';


export default function useOutageIntervalState(outageDay: string, selectedWebsite: WebsiteIndexEntry | undefined, outagesStartDate: string, outagesEndDate: string) {
    const [isFirstTimeSettingInterval, setIsFirstTimeSettingInterval] = useState<boolean>(true);
    const { data: outageList, isLoading: isLoadingOutageList, isSuccess: isSuccessOutageList } = useFetchOutageListQuery(
        selectedWebsite ? { url: selectedWebsite.homepage_url, startDate: outagesStartDate, endDate: outagesEndDate } : skipToken
    );
    const [fetchOutageContext, { isFetching: isLoadingOutageContext }] = useLazyFetchOutageContextQuery();

    const allOutagesGroupedByDay = useMemo(() => {
        const accumulatedOutages = outageList ? [...outageList.outages.archived] : [];
        if (outageList && Object.keys(outageList.outages.active).length) {
            const activeOutage = outageList.outages.active as Outage;
            const activeEvent = {
                start_at: moment.unix(activeOutage.created_at).utc().format('YYYY-MM-DDTHH:mm:ss'),
                end_at: moment().utc().format('YYYY-MM-DDTHH:mm:ss'),
                guid: activeOutage.guid,
            };

            accumulatedOutages.push(activeEvent);
        }
        return getOutagesGroupedByDays(accumulatedOutages);
    }, [outageList]);

    const outageIntervals = useMemo(() => getIntervalsForDay(outageDay, allOutagesGroupedByDay[outageDay]), [allOutagesGroupedByDay, outageDay]);
    const [selectedOutageInterval, setSelectedOutageInterval] = useState<HoursInterval>(outageIntervals[getLastIntervalIndex(outageIntervals)]);
    const [outageContext, setOutageContext] = useState<Outage | null>(null);

    const handleOutageIntervalChange = useCallback(
        async (newOutageInterval: HoursInterval) => {
            setSelectedOutageInterval(newOutageInterval);

            if (newOutageInterval.guid) {
                if (outageList && Object.keys(outageList.outages.active).length) {
                    const activeOutage = outageList.outages.active as Outage;

                    if (activeOutage.guid.localeCompare(newOutageInterval.guid) === 0) {
                        setOutageContext(activeOutage);
                        return;
                    }
                }
                try {
                    const fetchedOutageContext = await fetchOutageContext({ guid: newOutageInterval.guid }, true).unwrap();
                    setOutageContext(fetchedOutageContext);
                } catch { }
            } else {
                setOutageContext(null);
            }
        },
        [outageList, fetchOutageContext]
    );

    useEffect(() => {
        if (isSuccessOutageList && isFirstTimeSettingInterval) {
            setIsFirstTimeSettingInterval(false);
            handleOutageIntervalChange(outageIntervals[getLastIntervalIndex(outageIntervals)]);
        }
    }, [handleOutageIntervalChange, isFirstTimeSettingInterval, isSuccessOutageList, outageIntervals]);

    return {
        outagesGroupedByDay: allOutagesGroupedByDay,
        selectedOutageInterval,
        outageIntervals,
        selectedOutageContext: outageContext,
        isLoadingOutageContext,
        isLoadingOutageList,
        handleOutageIntervalChange,
    };
}
